// The data the core and the resident agent exchange through AgentHost:
// what a chat runs with, the entries it reads, what a run reports, the
// requests a person makes, and the turn items as the agent sees them.
import { z } from "zod";
import { AgentKind, OtherMcpMode } from "./capabilities";
import { signedShape } from "./instruction";
import {
  TURN_CLOSED_BY,
  TURN_STATES,
  TURN_STATUSES,
  TurnAnswer,
  TurnSpec,
} from "./turn";

const Id = z.string().min(1).max(200);

/**
 * A signed write the store keeps beside a row (`signed` of an entry, a
 * request, a chat's conditions, a device's settings). Optional: a store that
 * keeps none (before the trusted devices' list) leaves it out.
 */
const SignedField = signedShape.nullable().optional();

/** A file as the agent sees it. */
export const FileRef = z.object({
  id: Id,
  name: z.string().min(1).max(500),
  mime: z.string().max(200),
  size: z.number().int().min(0),
  /** SHA-256 of the content (hex); null for files from before hashes. */
  hash: z
    .string()
    .regex(/^[0-9a-f]{64}$/)
    .nullable()
    .optional(),
});
export type FileRef = z.infer<typeof FileRef>;

/** A chat named in another chat (family, turn items). */
export const ChatRef = z.object({
  id: Id,
  key: z.string().max(100),
  title: z.string().max(500).nullable(),
});
export type ChatRef = z.infer<typeof ChatRef>;

/** Most levels of chats under one top chat. */
export const CHILD_DEPTH_MAX = 3;
/** Most children one chat runs and waits for at once. */
export const CHILD_OPEN_MAX = 5;
/** Most characters of a reply (as of a post; a longer one is not stored). */
export const REPLY_TEXT_MAX = 100_000;

/**
 * What a chat is about. Only Cases for now: the Case's consulting chat has no
 * `step`; a chat delegated an Action has it.
 */
export const ChatSubject = z.object({
  kind: z.literal("case"),
  caseId: Id,
  caseTitle: z.string().max(500),
  step: z.object({ id: Id, title: z.string().max(500) }).nullable(),
});
export type ChatSubject = z.infer<typeof ChatSubject>;

export const ChildStatus = z.enum(["running", "queued", "waiting", "stopped"]);
export type ChildStatus = z.infer<typeof ChildStatus>;

/** Where the chat sits among parents and children. */
export const ChatFamily = z.object({
  /** The top chat is 1. */
  depth: z.number().int().min(1),
  parent: ChatRef.extend({ secretary: z.boolean() }).nullable(),
  /** A parent agent started this chat (false: the person did). */
  launchedByParent: z.boolean(),
  /** A Case's consulting chat works with the person directly even when a parent started it. */
  caseChat: z.boolean(),
  /** Whether this chat may start children (depth below the maximum). */
  canLaunch: z.boolean(),
  /** Children this chat started that are still active. */
  children: z.array(ChatRef.extend({ status: ChildStatus })).max(50),
});
export type ChatFamily = z.infer<typeof ChatFamily>;

/** An open turn item in brief, so the agent knows what a post might answer. */
export const TurnOpenBrief = z.object({
  id: Id,
  kind: z.string().max(50),
  title: z.string().max(500),
  status: z.enum(["queued", "shown", "seen"]),
  /** Set while the person asked to place the draft in Slack and no result came yet. */
  slackDraftRequestedAt: z.string().nullable(),
});
export type TurnOpenBrief = z.infer<typeof TurnOpenBrief>;

/**
 * A side chat's first run forks from a reply of the main chat. `session`
 * resumes that run's CLI session in a new branch; `history` starts fresh with
 * the main chat's text up to the reply.
 */
export const ChatFork = z.object({
  parentChatId: Id,
  parentTitle: z.string().max(500),
  entryId: Id,
  entryAt: z.string(),
  /** The run that wrote the reply forked from (its session is where a side chat goes on). */
  runId: Id.nullable().optional(),
  resume: z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("session"),
      sessionId: Id,
      agentKind: AgentKind,
    }),
    z.object({ kind: z.literal("history") }),
  ]),
});
export type ChatFork = z.infer<typeof ChatFork>;

/**
 * The conditions a chat runs with, decided by the chat row alone. `cwd` null
 * means no file access (an empty directory). `revision` bumps on each change
 * and is a run boundary.
 */
export const ChatSpec = z.object({
  agentKind: AgentKind,
  model: z.string().max(200).nullable(),
  effort: z.string().max(100).nullable(),
  cwd: z.string().max(1000).nullable(),
  otherMcp: OtherMcpMode,
  revision: z.number().int().min(0),
  subject: ChatSubject.nullable(),
  family: ChatFamily,
  turnOpen: z.array(TurnOpenBrief).max(50),
  fork: ChatFork.nullable(),
  /** The last `conditions` instruction the chat was given (the agent keeps its own copy). */
  signed: SignedField,
});
export type ChatSpec = z.infer<typeof ChatSpec>;

/** The chat itself, as `chats.work` returns it. */
export const ChatInfo = z.object({
  id: Id,
  key: z.string().max(100),
  title: z.string().max(500).nullable(),
  /** chat or secretary. */
  role: z.enum(["chat", "secretary"]),
  /** Highest seq the agent has read. */
  readSeq: z.number().int().min(0),
  lastSeq: z.number().int().min(0),
});
export type ChatInfo = z.infer<typeof ChatInfo>;

export const ENTRY_KINDS = [
  "message",
  "notice",
  "answer",
  "signal",
  "mark",
] as const;
export type EntryKind = (typeof ENTRY_KINDS)[number];
export const ENTRY_AUTHORS = ["user", "agent", "parent", "system"] as const;
export type EntryAuthor = (typeof ENTRY_AUTHORS)[number];

/**
 * One row of a chat: a post, a notice about a child, an answer to a turn
 * item, a signal that wakes the chat, or a mark (stopped, compacted). Rows are
 * numbered 1, 2, 3 per chat.
 */
export const Entry = z.object({
  id: Id,
  seq: z.number().int().min(1),
  kind: z.enum(ENTRY_KINDS),
  author: z.enum(ENTRY_AUTHORS),
  body: z.string(),
  /** Per kind: NoticeData, AnswerData, SignalData, MarkData; null for posts. */
  data: z.record(z.string(), z.unknown()).nullable(),
  files: z.array(FileRef).max(50),
  /** The chat it came from (a parent's instruction, a child's notice). */
  fromChatId: Id.nullable(),
  /** The screen the person was looking at when posting, one line. */
  context: z.string().max(1000).nullable(),
  createdAt: z.string(),
  /** The instruction it was written with (a post, an answer, a parent's instruction, a child's notice). */
  signed: SignedField,
});
export type Entry = z.infer<typeof Entry>;

export const NOTICE_KINDS = [
  "started",
  "ended",
  "stopped",
  "step_closed",
  "instructed",
  "retried",
  "read",
  "answered",
  "turn_added",
  "raised",
] as const;
export type NoticeKind = (typeof NOTICE_KINDS)[number];

/** `data` of a notice entry: what happened in a child chat. */
export const NoticeData = z.object({
  kind: z.enum(NOTICE_KINDS),
  child: ChatRef,
  by: z.enum(["user", "parent"]).nullable(),
  outcome: z.enum(["done", "asked", "failed"]).nullable(),
  /** Head of the child's last reply or of the instruction. */
  excerpt: z.string().nullable(),
  replyEntryId: Id.nullable(),
  askTitle: z.string().nullable(),
  error: z.string().nullable(),
  step: z.object({ id: Id, title: z.string(), status: z.string() }).nullable(),
  turnItemId: Id.nullable(),
});
export type NoticeData = z.infer<typeof NoticeData>;

export const SIGNAL_KINDS = [
  "step_ready",
  "wait_target",
  "turn_declined",
  "turn_slack_draft",
  "turn_withdrawn",
  "turn_awaiting",
] as const;
export type SignalKind = (typeof SIGNAL_KINDS)[number];

/** `data` of a signal entry: something the chat itself should act on. */
export const SignalData = z.object({
  kind: z.enum(SIGNAL_KINDS),
  turnItemId: Id.nullable(),
  /** step_ready: the steps that became current and why. */
  ready: z
    .object({
      caseId: Id,
      caseTitle: z.string(),
      steps: z.array(
        z.object({
          id: Id,
          kind: z.enum(["action", "waiting"]),
          title: z.string(),
        }),
      ),
      cause: z.object({
        by: z.enum(["human", "agent", "auto"]),
        steps: z.array(
          z.object({ id: Id, title: z.string(), status: z.string() }),
        ),
      }),
    })
    .nullable(),
  /** wait_target: what a waiting was waiting on changed. */
  waitTarget: z
    .object({
      caseId: Id,
      caseTitle: z.string(),
      event: z.enum(["dropped", "deleted", "reopened"]),
      target: z.object({
        kind: z.enum(["case", "action", "issue"]),
        id: Id,
        title: z.string().nullable(),
      }),
      steps: z.array(
        z.object({
          id: Id,
          title: z.string(),
          status: z.enum(["open", "done"]),
        }),
      ),
    })
    .nullable(),
});
export type SignalData = z.infer<typeof SignalData>;

/** `data` of an answer entry: an answer to one of this chat's turn items. */
export const AnswerData = z.object({
  itemId: Id,
  spec: TurnSpec,
  answer: TurnAnswer,
  by: z.enum(["human", "parent"]),
  answeredBy: ChatRef.nullable(),
  overrides: TurnAnswer.nullable(),
  humanEdited: z.boolean(),
});
export type AnswerData = z.infer<typeof AnswerData>;

/** `data` of a mark entry. */
export const MarkData = z.object({
  kind: z.enum(["stopped", "compacted", "rewound", "withdrawn"]),
  runId: Id.nullable(),
});
export type MarkData = z.infer<typeof MarkData>;

/** One earlier exchange, for a run that cannot resume a CLI session. */
export const HistoryItem = z.object({
  author: z.enum(ENTRY_AUTHORS),
  kind: z.enum(ENTRY_KINDS),
  body: z.string(),
  createdAt: z.string(),
});
export type HistoryItem = z.infer<typeof HistoryItem>;

/**
 * The session a chat's next run continues. null starts a new session (first
 * run, another device, another CLI, after a rewind to the start).
 */
export const ChatResume = z.object({
  sessionId: Id,
  agentKind: AgentKind,
  /** The run that left it (rewind and fork count from it). */
  runId: Id,
});
export type ChatResume = z.infer<typeof ChatResume>;

/**
 * Token usage of one run. The first four are the last turn; the session
 * totals add up the whole CLI session.
 */
export const RunUsage = z.object({
  inputTokens: z.number().int().min(0),
  cacheReadInputTokens: z.number().int().min(0),
  cacheCreationInputTokens: z.number().int().min(0),
  outputTokens: z.number().int().min(0),
  contextWindow: z.number().int().min(1).nullable(),
  model: z.string().max(200).nullable(),
  sessionInputTokens: z.number().int().min(0).nullable(),
  sessionCacheReadTokens: z.number().int().min(0).nullable(),
  sessionCacheCreationTokens: z.number().int().min(0).nullable(),
  sessionOutputTokens: z.number().int().min(0).nullable(),
});
export type RunUsage = z.infer<typeof RunUsage>;

/**
 * One input a run read: the first one (`initial`) or one injected while it
 * ran. `uuid` is the CLI transcript's message id (Claude), which a rewind of
 * files points at. null for Codex and before the CLI read it.
 */
export const RunInput = z.object({
  kind: z.enum(["initial", "inject"]),
  throughSeq: z.number().int().min(0),
  entryIds: z.array(Id).max(200),
  uuid: z.string().max(100).nullable(),
});
export type RunInput = z.infer<typeof RunInput>;

/** Changes a run made to trameria, shown under the reply. */
export const CHANGE_KINDS = [
  "case",
  "action",
  "waiting",
  "page",
  "issue",
  "chat",
  "message",
  "issue_comment",
  "delegation",
  "person",
  "job",
] as const;
export const CHANGE_OPS = [
  "created",
  "updated",
  "completed",
  "dropped",
  "reopened",
  "deleted",
] as const;
export const Change = z.object({
  kind: z.enum(CHANGE_KINDS),
  op: z.enum(CHANGE_OPS),
  id: z.string().min(1).max(100),
  title: z.string().max(300),
  /** The Case of an action, waiting or delegation. */
  caseId: z.string().max(100).nullable(),
  /** What opens it: a key, an issue ref, the delegated step. */
  key: z.string().max(200).nullable(),
});
export type Change = z.infer<typeof Change>;
export const CHANGES_MAX = 50;

/** How a run ended. */
export const RunEnd = z.object({
  status: z.enum(["ended", "stopped", "failed"]),
  /** Cost of this run (not the session's running total). */
  costUsd: z.number().nullable(),
  /**
   * Claude: the session's running total the CLI reported (`total_cost_usd`).
   * When given, the core works out this run's share from the session's
   * previous total and ignores `costUsd`.
   */
  sessionCostUsd: z.number().nullable().optional(),
  usage: RunUsage.nullable(),
  contextTokens: z.number().int().min(0).nullable(),
  /**
   * The session the run resumed (null for a new one: then the session the CLI
   * reported) and the branch this run wrote. The core groups the runs of one
   * session by the session's first id and continues from the branch.
   */
  sessionId: Id.nullable(),
  branchSessionId: Id.nullable(),
  inputs: z.array(RunInput).max(200),
  /** First line, at most 1000 characters. */
  error: z.string().max(1000).nullable(),
  /**
   * `interrupted` when an earlier process died during the run. With a branch
   * the CLI kept what the run read (the next run continues the branch);
   * without one the core puts what the run read back to unread.
   * `needs_workdir` when the run was refused for attachments with no working
   * directory: the core reads those posts again once one is chosen.
   */
  errorCode: z
    .enum(["interrupted", "login", "cli", "host", "needs_workdir"])
    .nullable(),
  durationMs: z.number().int().min(0),
});
export type RunEnd = z.infer<typeof RunEnd>;

/**
 * The live state of a running run. Not stored; readers fetch it again when
 * they need it.
 */
export const RunLive = z.object({
  /** One progress line (the progress tool), null to clear. */
  progress: z.string().max(200).nullable(),
  /** The tool being called now, null when none. */
  tool: z.string().max(200).nullable(),
  contextTokens: z.number().int().min(0).nullable(),
  compacting: z.boolean(),
  /** Waiting for the device's write lock, and who holds it. */
  waiting: z.object({ blockedBy: Id.nullable() }).nullable(),
});
export type RunLive = z.infer<typeof RunLive>;

export const REQUEST_KINDS = [
  "cancel",
  "read_now",
  "withdraw",
  "compact",
  "rewind",
  "rewind_preview",
] as const;
export type RequestKind = (typeof REQUEST_KINDS)[number];

/** Files of a rewind: the transcript message to go back to and its session. */
const RewindFiles = z.object({ uuid: z.string().max(100), sessionId: Id });

/** A person's request about a chat's run, settled by the agent. */
export const Request = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("cancel"),
    id: Id,
    chatId: Id,
    /** The run to stop; null stops what has not started yet up to `throughSeq`. */
    runId: Id.nullable(),
    throughSeq: z.number().int().min(0),
    createdAt: z.string(),
    signed: SignedField,
  }),
  z.object({
    kind: z.literal("read_now"),
    id: Id,
    chatId: Id,
    runId: Id,
    entryId: Id,
    createdAt: z.string(),
    signed: SignedField,
  }),
  z.object({
    kind: z.literal("withdraw"),
    id: Id,
    chatId: Id,
    runId: Id,
    entryId: Id,
    createdAt: z.string(),
    signed: SignedField,
  }),
  z.object({
    kind: z.literal("compact"),
    id: Id,
    chatId: Id,
    runId: Id.nullable(),
    createdAt: z.string(),
    signed: SignedField,
  }),
  z.object({
    kind: z.literal("rewind"),
    id: Id,
    chatId: Id,
    runId: Id.nullable(),
    /** Entries after this are taken back; later posts wake the next run. */
    throughSeq: z.number().int().min(0),
    files: RewindFiles.nullable(),
    /** Whether the conversation is rewound too (the next run's session comes from `chats.work`). */
    conversation: z.boolean(),
    createdAt: z.string(),
    signed: SignedField,
  }),
  z.object({
    kind: z.literal("rewind_preview"),
    id: Id,
    chatId: Id,
    runId: Id.nullable(),
    files: RewindFiles,
    createdAt: z.string(),
    signed: SignedField,
  }),
]);
export type Request = z.infer<typeof Request>;

/** Files reverted (or, for a preview, that would be). */
export const RewindFilesReport = z.discriminatedUnion("ok", [
  z.object({
    ok: z.literal(true),
    filesChanged: z.array(z.string().max(1000)).max(500),
    insertions: z.number().int().min(0),
    deletions: z.number().int().min(0),
  }),
  z.object({
    ok: z.literal(false),
    reason: z.enum([
      "no_checkpoint",
      "session_switched",
      "unsupported",
      "failed",
    ]),
    message: z.string().max(2000).nullable(),
  }),
]);
export type RewindFilesReport = z.infer<typeof RewindFilesReport>;

/** How the agent settled a request. */
export const RequestResult = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("cancel"),
    /** cancelled: stopped a run or posts before it. finished: nothing was running any more. */
    outcome: z.enum(["cancelled", "finished"]),
    /** Posts taken back because no run had read them. */
    withdrawnEntryIds: z.array(Id).max(200),
    /** No later posts queued, so the text may go back to the input field. */
    restore: z.boolean(),
    /** The stopped run was a compaction. */
    compaction: z.boolean(),
  }),
  z.object({
    kind: z.literal("read_now"),
    outcome: z.enum(["read", "too_late", "not_running"]),
  }),
  z.object({
    kind: z.literal("withdraw"),
    outcome: z.enum(["withdrawn", "too_late"]),
  }),
  z.object({
    kind: z.literal("compact"),
    ok: z.boolean(),
    /** busy / no_session refuse without running. */
    error: z.string().max(1000).nullable(),
    runId: Id.nullable(),
    costUsd: z.number().nullable(),
    contextTokensBefore: z.number().int().min(0).nullable(),
    contextTokensAfter: z.number().int().min(0).nullable(),
  }),
  z.object({
    kind: z.literal("rewind"),
    files: RewindFilesReport.nullable(),
    error: z.string().max(2000).nullable(),
  }),
  z.object({
    kind: z.literal("rewind_preview"),
    files: RewindFilesReport,
  }),
]);
export type RequestResult = z.infer<typeof RequestResult>;

/** A turn item as the agent sees it (tools and their results). */
export const TurnItem = z.object({
  id: Id,
  /** The chat that put it in the list. */
  chatId: Id,
  /** The chat whose list shows it now (a parent may hold or raise it). */
  holderChatId: Id,
  kind: z.string().max(50),
  spec: TurnSpec,
  state: z.enum(TURN_STATES),
  status: z.enum(TURN_STATUSES),
  /** Place among the holder's open items (0 is shown). null when closed. */
  place: z.number().int().min(0).nullable(),
  version: z.number().int().min(0),
  answer: TurnAnswer.nullable(),
  note: z.string().nullable(),
  closedBy: z.enum(TURN_CLOSED_BY).nullable(),
  case: z
    .object({ id: Id, title: z.string(), dueAt: z.string().nullable() })
    .nullable(),
  files: z.array(FileRef).max(50),
  /** Earlier contents; human means the person edited a draft. */
  revisions: z
    .array(
      z.object({
        cause: z.enum(["conversation", "approved", "human"]),
        reason: z.string(),
        revisedAt: z.string(),
      }),
    )
    .max(100),
  /** A rewrite waiting for the person's consent. */
  proposal: z
    .object({ title: z.string(), reason: z.string(), proposedAt: z.string() })
    .nullable(),
  /** The chat answering it when it is not the owner (a parent deciding or asking). */
  heldBy: ChatRef.nullable(),
  /** For an item raised from a child: the child chat. */
  from: ChatRef.nullable(),
  /** The parent chat that answered on the person's behalf. */
  answeredBy: ChatRef.nullable(),
  slackDraft: z
    .object({
      state: z.enum(["requested", "placed", "failed"]),
      requestedAt: z.string(),
      version: z.number().int().min(0),
      url: z.string().nullable(),
      error: z.string().nullable(),
    })
    .nullable(),
  seenAt: z.string().nullable(),
  closedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type TurnItem = z.infer<typeof TurnItem>;
